import * as XLSX from 'xlsx';
import { parseResultsData, parseConsolidatedTocData } from './dataProcessor';

// --- Program/Accelerator description context documents ---
// Maps SP code → filename in public/data/descriptions/
const DESCRIPTION_FILES = {
  SP01: 'Breeding_for_Tomorrow_Context.md',
  SP02: 'SFP_Comprehensive_Context_Document.md',
  SP03: 'SAAF_Comprehensive_Context_Document.md',
  SP04: 'MFL_SP_Context_Document.md',
  SP05: 'Better_Diets_and_Nutrition_Context_Document.md',
  SP06: 'Climate_Action_Context_Document.md',
  SP07: 'Policy_Innovations_Science_Program_Context_Document.md',
  SP08: 'Food_Frontiers_and_Security_Context_Document.md',
  SP09: 'Scaling_for_Impact_Context_Document.md',
  SP10: 'Gender_Equality_and_Inclusion_Accelerator_Context_Document.md',
  SP11: 'CapSha_Accelerator_Context.md',
  SP12: 'Digital_Transformation_Accelerator_Context_Document.md',
  SP13: 'Genebanks_Accelerator_Context_Document.md',
};

// --- Impact Area description context documents ---
// Maps IA key → filename in public/data/descriptions/
const IA_DESCRIPTION_FILES = {
  gender:       'IA_Gender_Equality_Youth_and_Social_Inclusion.md',
  climate:      'IA_Climate_Adaptation_and_Mitigation.md',
  nutrition:    'IA_Nutrition_Health_and_Food_Security.md',
  biodiversity: 'IA_Environmental_Health_and_Biodiversity.md',
  poverty:      'IA_Poverty_Reduction_Livelihoods_and_Jobs.md',
};

// --- Pre-processed pipeline output (preferred) ---
const PROCESSED_FILE = '/data/processed.json';

// --- Raw Excel paths (fallback if processed.json doesn't exist or is stale) ---
const RESULTS_FILE = '/data/results/All_Resporting_Results_20260326_SS_JD_JC_AJ_V3_clean.xlsx';
const EXPECTED_RESULTS_BASENAME = 'All_Resporting_Results_20260326_SS_JD_JC_AJ_V3_clean.xlsx';
const TOC_FILE = '/data/toc/P25 - All ToC.xlsx';

async function fetchAndParseExcel(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
  const arrayBuffer = await response.arrayBuffer();
  return XLSX.read(arrayBuffer);
}

/**
 * Try to load the pre-processed JSON file produced by the pipeline.
 * Returns { results, tocData, filterOptions } or null if not available.
 */
async function loadProcessedData() {
  try {
    const response = await fetch(PROCESSED_FILE);
    if (!response.ok) return null;

    const data = await response.json();

    // Validate structure
    if (!data.results || !data.toc || !data.filterOptions) {
      console.warn('processed.json has unexpected structure, falling back to Excel');
      return null;
    }

    // Staleness check: verify the pipeline was built from the same results file
    // the app expects. Prevents silently serving old data after a source update.
    const pipelineSource = data.meta?.resultsFile;
    if (!pipelineSource) {
      console.warn(
        'processed.json has no resultsFile metadata (old pipeline version). ' +
        'Falling back to Excel. Run "npm run pipeline" to regenerate.'
      );
      return null;
    }
    if (pipelineSource !== EXPECTED_RESULTS_BASENAME) {
      console.warn(
        `processed.json is stale: built from "${pipelineSource}" but app expects ` +
        `"${EXPECTED_RESULTS_BASENAME}". Falling back to Excel. ` +
        `Run "npm run pipeline" to regenerate.`
      );
      return null;
    }

    console.log(
      `Loaded pre-processed data (pipeline v${data.meta?.pipelineVersion || '?'}, ` +
      `${data.results.length} results, generated ${data.meta?.generatedAt || '?'})`
    );

    return data;
  } catch (err) {
    console.warn('Could not load processed.json, falling back to Excel:', err.message);
    return null;
  }
}

/**
 * Load the program description markdown for a given SP code.
 * Returns the markdown string, or null if no description is available.
 */
export async function loadProgramDescription(programCode) {
  const filename = DESCRIPTION_FILES[programCode];
  if (!filename) return null;

  try {
    const response = await fetch(`/data/descriptions/${filename}`);
    if (!response.ok) return null;
    return await response.text();
  } catch (err) {
    console.warn(`Could not load description for ${programCode}:`, err.message);
    return null;
  }
}

/**
 * Load an Impact Area description markdown.
 * Returns the markdown string, or null if unavailable.
 */
export async function loadIADescription(iaKey) {
  const filename = IA_DESCRIPTION_FILES[iaKey];
  if (!filename) return null;

  try {
    const response = await fetch(`/data/descriptions/${filename}`);
    if (!response.ok) return null;
    return await response.text();
  } catch (err) {
    console.warn(`Could not load IA description for ${iaKey}:`, err.message);
    return null;
  }
}

/**
 * Load ALL Impact Area descriptions in parallel.
 * Returns an object { gender: '...', climate: '...', ... } with nulls for missing.
 */
export async function loadAllIADescriptions() {
  const keys = Object.keys(IA_DESCRIPTION_FILES);
  const results = await Promise.all(keys.map(k => loadIADescription(k)));
  const map = {};
  keys.forEach((k, i) => { map[k] = results[i]; });
  return map;
}

export async function autoLoadData({ setResultsData, setTocData, setLoading, setError, setFilterOptions }) {
  setLoading(true);
  try {
    // Try pre-processed pipeline output first
    const processed = await loadProcessedData();

    if (processed) {
      // Load from pipeline output — no client-side parsing needed
      setResultsData(processed.results, processed.filterOptions);

      // Load TOC data from pipeline
      for (const [code, tocParsed] of Object.entries(processed.toc)) {
        setTocData(code, tocParsed);
      }

      return;
    }

    // Fallback: load raw Excel files (legacy path)
    console.log('Loading from raw Excel files (no processed.json found)');

    const resultsWorkbook = await fetchAndParseExcel(RESULTS_FILE);
    const results = parseResultsData(resultsWorkbook);
    setResultsData(results);

    // Load consolidated TOC file
    try {
      const tocWorkbook = await fetchAndParseExcel(TOC_FILE);
      const allTocData = parseConsolidatedTocData(tocWorkbook);
      for (const [code, tocParsed] of Object.entries(allTocData)) {
        setTocData(code, tocParsed);
      }
    } catch (err) {
      console.warn('Failed to load consolidated TOC file:', err);
    }
  } catch (error) {
    console.error('Auto-load failed:', error);
    setError('Auto-loading data failed. You can still upload files manually.');
  } finally {
    setLoading(false);
  }
}
